import React, {createRef, ReactNode, RefObject} from "react";
import AbstractViewer, {AbstractViewerProperties, AbstractViewerState} from "../AbstractViewer";
import {ViewerMode} from "../ViewerMode";
import MouseMode from "../settings/MouseMode";
import MessageBox from "../../../shared/components/MessageBox";
import i18next from "i18next";
import DomHelper from "../../../shared/helper/DomHelper";
import Exception from "../../../shared/exception/Exception";
import PortalDocument from "../../document/PortalDocument";
import {RTFJS} from "rtf.js";
import {MessageType} from "../../../shared/log/MessageInterface";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../../shared/helper/FunctionHelper";

export interface RtfViewerProperties extends AbstractViewerProperties {
}

export interface RtfViewerState extends AbstractViewerState {
	viewerData: string;
	loading: boolean;
	error?: string;
}

export class RtfViewerDefinition extends AbstractViewer<RtfViewerProperties, RtfViewerState> {
	private loaderElement: RefObject<HTMLDivElement>;

	public static defaults(): RtfViewerProperties {
		return {
			viewerMode: ViewerMode.DEFAULT,
			mouseMode: MouseMode.NONE
		} as RtfViewerProperties;
	}

	constructor(props: Readonly<RtfViewerProperties>) {
		super(props);

		this.state = {
			...this.state,
			loading: true
		};

		this.loaderElement = createRef<HTMLDivElement>();
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		if (this.loaderElement.current !== null) {
			// Add active class after loader is mounted so the fade animation is applied
			DomHelper.addClass(this.loaderElement.current, "active");
		}

		try {
			await super.componentDidMount();
			await this.updateViewerData();
		} catch (e: any) {
			let error: Exception = e;

			this.setState({
				loading: false,
				error: error.message
			});
			return;
		}

		this.setState({
			loading: false,
			error: undefined
		});
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		this.setState({
			loading: true,
			error: undefined
		});

		await super.componentWillUnmount();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<RtfViewerProperties>, prevState: Readonly<RtfViewerState>
	): Promise<void> {
		if (this.state.loading) {
			return;
		}

		let documentChanged: Array<boolean> = [
			this.props.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
			this.props.portalDocument.get().updated !== prevProps.portalDocument?.get().updated ||
			this.props.attachmentKey !== prevProps.attachmentKey
		];

		if (documentChanged.includes(true)) {
			this.setState({
				loading: true,
				error: undefined
			});

			try {
				await super.componentWillUnmount();
				await super.componentDidMount();
				await this.updateViewerData();
			} catch (e: any) {
				let error: Exception = e;

				this.setState({
					loading: false,
					error: error.message
				});
				return;
			}

			this.setState({
				loading: false,
				error: undefined
			});
		}
	}

	private async updateViewerData(): Promise<void> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let rtfDocument: RTFJS.Document;
		if (this.props.viewerMode === ViewerMode.PORTFOLIO || this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
			rtfDocument = await portalDocument.loadViewerData(attachment);
		} else {
			rtfDocument = await portalDocument.loadViewerData();
		}

		let elements: Array<HTMLElement> = await rtfDocument.render();
		let viewerData: string = "";

		for (let element of elements) {
			viewerData += element.outerHTML;
		}

		this.setState({
			viewerData: viewerData
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.loading) {
			return (
				<div className="viewer rtf" ref={this.viewerElement}>
					<div className="loader" ref={this.loaderElement}>
						<span className="icon icon-spinner spin"/>
					</div>
				</div>
			);
		}

		if (FunctionHelper.exists(this.state.error)) {
			return (
				<div className="viewer rtf" ref={this.viewerElement}>
					{(this.props.viewerMode === ViewerMode.PREVIEW || this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW) &&
						<div className="fallback-icon">
							<span className="icon icon-text"/>
						</div>
					}
					{this.props.viewerMode !== ViewerMode.PREVIEW && this.props.viewerMode !== ViewerMode.PORTFOLIO_PREVIEW &&
						<MessageBox type={MessageType.DANGER} language={i18next.language}>
							{this.state.error}
						</MessageBox>
					}
				</div>
			);
		}

		if (this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			return (
				<div className="fallback-icon">
					<span className="icon icon-text"/>
				</div>
			);
		}

		return (
			<div className="viewer rtf" ref={this.viewerElement}>
				<div className="rtf-viewer move" onPointerDown={
					(event) => this.handleMouseDown(event.nativeEvent)
				} dangerouslySetInnerHTML={{__html: this.state.viewerData}}/>
			</div>
		);
	}
}

const RtfViewer = React.memo(RtfViewerDefinition,
	(prevProps: Readonly<RtfViewerProperties>, nextProps: Readonly<RtfViewerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument) &&
			(
				nextProps.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
				nextProps.portalDocument.get().updated !== prevProps.portalDocument?.get().updated
			)
		) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		return true;
	}
);

export default RtfViewer;